// Thin client for the chat-api. Mirrors the shape of api/client.ts but points
// at a different base URL (chat-api lives on its own Fly app). Reuses the
// admin access token from localStorage — chat-api verifies the same JWT
// secret as admin-api, so no separate login is needed.

import type {
  Conversation,
  ConversationWithMessages
} from "@attendik/types/chat-schemas";

import { getToken } from "./client.js";
import { ApiError, ScopeError } from "./errors.js";

const BASE_URL = import.meta.env.VITE_CHAT_API_BASE_URL ?? "";
const API_PREFIX = "/chat/v1";

function url(path: string): string {
  return `${BASE_URL}${API_PREFIX}${path}`;
}

function clientHeader(): string | null {
  if (typeof navigator !== "undefined" && navigator.userAgent) {
    return navigator.userAgent.slice(0, 200);
  }
  return null;
}

async function requestJson<T>(
  path: string,
  options: { method?: string; body?: unknown } = {}
): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  const ua = clientHeader();
  if (ua) {
    headers["X-Attendik-Client"] = ua;
  }
  let response: Response;
  try {
    response = await fetch(url(path), {
      method: options.method ?? "GET",
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body)
    });
  } catch {
    throw new ApiError(0, "NETWORK", "Could not reach the chat service.");
  }

  // A 401 from chat-api does NOT mean the admin session is invalid — it
  // means chat-api can't verify the token (usually because its
  // ADMIN_JWT_SECRET doesn't match admin-api's). Surface a clear error
  // and stay on the page; do not force-logout the user.
  if (response.status === 401) {
    throw new ApiError(
      401,
      "UNAUTHORIZED",
      "Chat service rejected the session. ADMIN_JWT_SECRET likely doesn't match the admin app."
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const envelope = payload as { error?: string; code?: string } | null;
    const code = envelope?.code ?? "ERROR";
    const message = envelope?.error ?? "Request failed";
    if (response.status === 403) {
      throw new ScopeError(message);
    }
    throw new ApiError(response.status, code, message);
  }

  return payload as T;
}

// ----------------------------------------------------------------------------
// SSE event types — mirror the SseEvent union in chat-api's toolRunner.
// ----------------------------------------------------------------------------

export type ChatSseEvent =
  | { event: "message_start"; data: { message_id: string } }
  | { event: "text_delta"; data: { delta: string } }
  | {
      event: "tool_use_start";
      data: { tool_use_id: string; tool_name: string; tool_call_id: string };
    }
  | {
      event: "tool_result";
      data: { tool_use_id: string; result: unknown; is_error: boolean };
    }
  | {
      event: "tool_pending_confirmation";
      data: {
        tool_call_id: string;
        tool_use_id: string;
        tool_name: string;
        tool_input: Record<string, unknown>;
      };
    }
  | {
      event: "message_end";
      data: {
        message_id: string;
        stop_reason: string | null;
        usage: unknown;
      };
    }
  | { event: "error"; data: { error: string; code: string } };

export interface ConfirmationResponse {
  status: "EXECUTED" | "FAILED" | "CANCELLED";
  tool_result: unknown;
  is_error: boolean;
}

/**
 * Opens an SSE stream over `fetch` (the browser EventSource API can't send
 * an Authorization header, so we read the body manually). Yields parsed
 * `ChatSseEvent`s. The caller should `for await` the iterator and close
 * via the returned AbortController on unmount.
 */
export function streamMessage(
  conversationId: string,
  message: string,
  signal: AbortSignal
): AsyncIterable<ChatSseEvent> & { close(): void } {
  const token = getToken();
  const ctrl = new AbortController();
  signal.addEventListener("abort", () => ctrl.abort());

  async function* iterator(): AsyncGenerator<ChatSseEvent> {
    const ua = clientHeader();
    const response = await fetch(
      url(`/conversations/${conversationId}/messages`),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(ua ? { "X-Attendik-Client": ua } : {})
        },
        body: JSON.stringify({ message }),
        signal: ctrl.signal
      }
    );

    if (response.status === 401) {
      throw new ApiError(
        401,
        "UNAUTHORIZED",
        "Chat service rejected the session. ADMIN_JWT_SECRET likely doesn't match the admin app."
      );
    }
    if (!response.ok || !response.body) {
      const text = await response.text().catch(() => "");
      let code = "ERROR";
      let errMsg = "The chat service refused the request.";
      if (text) {
        try {
          const env = JSON.parse(text) as { error?: string; code?: string };
          code = env.code ?? code;
          errMsg = env.error ?? errMsg;
        } catch {
          // ignore
        }
      }
      throw new ApiError(response.status, code, errMsg);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        buffer += decoder.decode(value, { stream: true });
        // SSE events are separated by a blank line.
        let sepIdx;
        while ((sepIdx = buffer.indexOf("\n\n")) >= 0) {
          const raw = buffer.slice(0, sepIdx);
          buffer = buffer.slice(sepIdx + 2);
          const evt = parseSse(raw);
          if (evt) {
            yield evt;
          }
        }
      }
    } finally {
      try {
        reader.releaseLock();
      } catch {
        // ignore
      }
    }
  }

  const it = iterator();
  return {
    [Symbol.asyncIterator]() {
      return it;
    },
    close() {
      ctrl.abort();
    }
  };
}

function parseSse(raw: string): ChatSseEvent | null {
  let event: string | null = null;
  let data: string | null = null;
  for (const line of raw.split("\n")) {
    if (line.startsWith(": ")) {
      continue; // keep-alive comment
    }
    if (line.startsWith("event: ")) {
      event = line.slice("event: ".length).trim();
    } else if (line.startsWith("data: ")) {
      data = (data ?? "") + line.slice("data: ".length);
    }
  }
  if (!event || data === null) {
    return null;
  }
  try {
    const parsed = JSON.parse(data) as unknown;
    return { event, data: parsed } as ChatSseEvent;
  } catch {
    return null;
  }
}

// ----------------------------------------------------------------------------
// Typed API surface
// ----------------------------------------------------------------------------

export const chatApi = {
  listConversations(): Promise<Conversation[]> {
    return requestJson<Conversation[]>("/conversations");
  },
  createConversation(): Promise<Conversation> {
    return requestJson<Conversation>("/conversations", { method: "POST", body: {} });
  },
  getConversation(id: string): Promise<ConversationWithMessages> {
    return requestJson<ConversationWithMessages>(`/conversations/${id}`);
  },
  confirmToolCall(
    id: string,
    approved: boolean,
    overrideInput?: Record<string, unknown>
  ): Promise<ConfirmationResponse> {
    return requestJson<ConfirmationResponse>(`/tool-calls/${id}/confirmation`, {
      method: "POST",
      body: { approved, tool_input: overrideInput }
    });
  }
};
